import { useContext, useEffect, useState } from "react";

import { loadGoogleMaps } from "../services/loadGoogleMaps";
import {
  COUNTRY_SPECIFIC_REQUIREMENTS,
  CURRENT_COUNTRY_IN_PRODUCTION,
} from "../../../../../../../../config/config";
import { ProcessContext } from "../../../../../../../../contexts/ProcessContext";
import { docIdTypes } from "../../../../config";

export const useGoogleMaps = () => {
  const {
    state: { documentType },
  } = useContext(ProcessContext);
  const [geocoder, setGeocoder] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);

  useEffect(() => {
    // Create clean up function to remove the script from the DOM if already added
    return () => {
      const script = existingScript();
      if (script) {
        script.remove();
      }
    };
  }, []);

  const existingScript = () => document.querySelector("#google-maps");

  const load = async (apiKey, language = "es") => {
    await loadGoogleMaps(apiKey, language);
    await initializeServices();
  };

  const initializeServices = async () => {
    // eslint-disable-next-line
    if (!google) {
      return console.error("Google not found");
    }
    // eslint-disable-next-line
    const { AutocompleteService } = await google.maps.importLibrary("places");
    // eslint-disable-next-line
    const { Geocoder } = await google.maps.importLibrary("geocoding");

    setAutocomplete(new AutocompleteService());
    setGeocoder(new Geocoder());
  };

  const retrievePredictions = async (input) => {
    const predictionsInput = {
      input,
      ...(documentType !== docIdTypes.PASSPORT && {
        componentRestrictions: {
          country:
            COUNTRY_SPECIFIC_REQUIREMENTS[CURRENT_COUNTRY_IN_PRODUCTION]
              .countryId,
        },
      }),
    };

    return new Promise((resolve, reject) => {
      if (!autocomplete) {
        return reject(new Error("Autocomplete service not found"));
      }
      autocomplete.getPlacePredictions(
        predictionsInput,
        (predictions, status) => {
          if (
            // eslint-disable-next-line
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            const matches = [];
            predictions.forEach((prediction) => {
              const { types } = prediction;
              if (
                types &&
                (types.includes("street_address") ||
                  types.includes("premise") ||
                  types.includes("route"))
              ) {
                matches.push(prediction);
              }
            });
            if (matches.length === 0) {
              return reject(new Error("No predictions found after filtering"));
            }
            return resolve(matches);
          } else {
            return reject(new Error("No predictions found"));
          }
        }
      );
    });
  };

  const geocodePlace = async (placeId) => {
    return new Promise((resolve, reject) => {
      if (!geocoder) {
        return reject(new Error("Geocoder service not found"));
      }

      geocoder.geocode({ placeId }, (results, status) => {
        // eslint-disable-next-line
        if (status === google.maps.GeocoderStatus.OK && results) {
          return resolve(results[0]);
        } else {
          return reject(new Error("No results found"));
        }
      });
    });
  };

  return {
    load,
    retrievePredictions,
    geocodePlace,
  };
};
